import { useState } from 'react';
import { Grid, Card, CardHeader, CardContent, useTheme, Tooltip } from '@mui/material';
import EditRounded from '@mui/icons-material/EditRounded';
import DeleteRounded from '@mui/icons-material/DeleteRounded';
import SearchRoundedIcon from '@mui/icons-material/SearchRounded';
import AttachEmailRoundedIcon from '@mui/icons-material/AttachEmailRounded';
import CreditCardRoundedIcon from '@mui/icons-material/CreditCardRounded';
import PaymentsRoundedIcon from '@mui/icons-material/PaymentsRounded';
import NewspaperRoundedIcon from '@mui/icons-material/NewspaperRounded';
import MoveDownRoundedIcon from '@mui/icons-material/MoveDownRounded';
import AttachMoneyRoundedIcon from '@mui/icons-material/AttachMoneyRounded';
import StickyNote2RoundedIcon from '@mui/icons-material/StickyNote2Rounded';
import styled from '@emotion/styled';
import { Link } from 'react-router-dom';

import SimplePagination from '../../../utils/utilities/SimplePagination';
import useSimpleTable from '../../../hooks/useSimpleTable';
import { usePageData } from '../../../contexts/PageData';
import { BasicFlex, LabelUpper } from '../../../utils/Common';
import { formatGenericDate, formatCurrency } from '../../../utils/util';
import DownloadButton from '../../../utils/utilities/Buttons/DownloadButton';
import MultiStatusChip from '../../../utils/utilities/MultiStatusChip';
import useIsMobile from '../../../hooks/useIsMobile';

import DeleteDialog from '../../../utils/utilities/DeleteDialog';

const MAX_ALLOCATIONS_SHOWN = 3;
const CARD_SPACING = 2; // MUI spacing units (16px)

const methods = [
  { label: "Credit Card", value: "credit_card", Icon: CreditCardRoundedIcon },
  { label: "Cash", value: "cash", Icon: PaymentsRoundedIcon },
  { label: "Check", value: "check", Icon: NewspaperRoundedIcon },
  { label: "Quick Pay", value: "quick pay", Icon: MoveDownRoundedIcon },
  { label: "Other", value: "other", Icon: AttachMoneyRoundedIcon },
];

const statusValues = [
  { value: "unapplied", label: "Unapplied", status: "error" },
  { value: "partially_applied", label: "Partially Applied", status: "warning" },
  { value: "applied", label: "Applied", status: "success" },
];

const getAmounts = (payment) => {
  const amount = Number(payment.amount || 0);
  const available = Number(payment.available_amount || 0);
  const applied = Math.max(amount - available, 0);
  return { amount, available, applied };
};

const getPaymentStatus = ({ amount, available, applied }) => {
  if (applied <= 0 && amount > 0) return "unapplied";
  if (available > 0) return "partially_applied";
  return "applied";
};

const formatMethod = (payment) => {
  const method = methods.find(m => m.value === payment.method) || methods[methods.length - 1];
  const ref = String(payment.info ?? '').trim();
  let text = method.label;
  if (ref) {
    if (payment.method === "credit_card") text = `Card ••${ref}`;
    else if (payment.method === "check") text = `Check #${ref}`;
    else text = `${method.label} · ${ref}`;
  }
  return { Icon: method.Icon, text };
};

const PaymentList = styled(BasicFlex)`
  margin-bottom: 1rem;
`;

const ActionsBarStyles = styled(BasicFlex)`
  border: 1px solid ${props => props.theme.colors.border};
  border-radius: 6px;
  overflow: hidden;
`;

const actionItemStyles = props => `
  display: flex;
  width: 3.5rem;
  height: 3.5rem;
  align-items: center;
  justify-content: center;
  background-color: transparent;
  color: ${props.theme.palette?.text?.secondary || '#666'};
  border-right: 1px solid ${props.theme.colors.border};
  cursor: pointer;
  transition: background-color .15s, color .15s;

  &:last-child {
    border-right: none;
  }

  && .MuiSvgIcon-root {
    color: inherit;
  }

  &:hover {
    background-color: ${props.theme.palette?.action?.hover || 'rgba(0,0,0,.04)'};
    color: ${props.$hoverColor || props.theme.palette?.text?.primary || '#222'};
  }
`;

const ActionItem = styled(BasicFlex)`
  ${props => actionItemStyles(props)}
`;

const LinkItem = styled(Link)`
  ${props => actionItemStyles(props)}
`;

const TitleRow = styled(BasicFlex)`
  justify-content: flex-start;
  align-items: center;
  gap: 1.2rem;
  flex-wrap: wrap;
`;

const Status = styled.div`
  font-size: 1.2rem;
`;

const AllocationsBlock = styled.div`
  padding: 1.2rem 0;
  border-top: 1px solid ${props => props.theme.colors.border};
  border-bottom: 1px solid ${props => props.theme.colors.border};
  font-size: 1.4rem;
`;

const AllocationRow = styled.div`
  display: flex;
  justify-content: space-between;
  gap: 1.2rem;
  padding: .3rem 0;
`;

const AllocationLabel = styled.span`
  display: inline-block;
  min-width: 7.5rem;
  margin-right: .8rem;
  color: ${props => props.theme.palette?.text?.secondary || '#666'};
`;

const InvoiceLink = styled(Link)`
  text-decoration: none;
  color: ${props => props.theme.palette?.info?.main || 'inherit'};

  &:hover {
    text-decoration: underline;
  }
`;

const Muted = styled.span`
  flex-shrink: 0;
  color: ${props => props.theme.palette?.text?.secondary || '#666'};
`;

const Memo = styled.div`
  display: flex;
  align-items: center;
  gap: .6rem;
  margin-top: .4rem;
  font-size: 1.3rem;
  color: ${props => props.theme.palette?.text?.secondary || '#666'};
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
`;

const MoreToggle = styled.button`
  margin-top: .4rem;
  padding: 0;
  border: none;
  background: none;
  font-size: 1.3rem;
  cursor: pointer;
  color: ${props => props.theme.palette?.info?.main || 'inherit'};

  &:hover {
    text-decoration: underline;
  }
`;

const MethodValue = styled.div`
  display: flex;
  align-items: center;
  gap: .6rem;

  .MuiSvgIcon-root {
    color: ${props => props.theme.palette?.text?.secondary || '#666'};
  }
`;

const Allocations = ({ allocations = [], notes }) => {
  const [expanded, setExpanded] = useState(false);
  if (!allocations.length && !notes) return null;

  const shown = expanded ? allocations : allocations.slice(0, MAX_ALLOCATIONS_SHOWN);
  const hiddenCount = allocations.length - MAX_ALLOCATIONS_SHOWN;

  return (
    <AllocationsBlock>
      {shown.map((a, i) => (
        <AllocationRow key={a.id ?? i}>
          <span>
            <AllocationLabel style={{ visibility: i === 0 ? 'visible' : 'hidden' }}>Applied to</AllocationLabel>
            <InvoiceLink to={`/invoices/view/${a.invoice_id}`}>
              Invoice #{a.invoice_num ?? a.invoice_id}
            </InvoiceLink>
          </span>
          <Muted>{formatCurrency(a.amount)}</Muted>
        </AllocationRow>
      ))}
      {hiddenCount > 0 && (
        <MoreToggle type="button" onClick={() => setExpanded(e => !e)}>
          {expanded ? 'Show less' : `+${hiddenCount} more ${hiddenCount === 1 ? 'invoice' : 'invoices'}`}
        </MoreToggle>
      )}
      {notes && (
        <Memo title={notes}>
          <StickyNote2RoundedIcon sx={{ fontSize: '1.5rem' }} />
          {notes}
        </Memo>
      )}
    </AllocationsBlock>
  );
};

const ActionsBar = ({ payment }) => {
  const theme = useTheme();
  return (
    <ActionsBarStyles>
      <Tooltip title="View Payment">
        <LinkItem $hoverColor={theme.palette.info.main} to={`/payments/view/${payment.payment_id}`}>
          <SearchRoundedIcon fontSize="small" />
        </LinkItem>
      </Tooltip>
      {/* <ActionItem $hoverColor={theme.palette.success.main}>
        <DownloadButton noColor url={`/payments/receipt/${payment.payment_id}`} title="Print Receipt" />
      </ActionItem>
      <Tooltip title="Email Receipt">
        <ActionItem $hoverColor={theme.palette.primary.main}>
          <AttachEmailRoundedIcon fontSize="small" />
        </ActionItem>
      </Tooltip> */}
      <Tooltip title="Edit Payment">
        <LinkItem $hoverColor={theme.palette.secondary.main} to={`/payments/edit/${payment.payment_id}`}>
          <EditRounded fontSize="small" />
        </LinkItem>
      </Tooltip>
      <Tooltip title="Delete Payment">
        <Tooltip title="Delete Payment">
        <DeleteDialog url="payments" id={payment.payment_id} toggle={<ActionItem $hoverColor={theme.palette.error.main}>
          <DeleteRounded fontSize="small" />
        </ActionItem>}
        />
      </Tooltip>
      </Tooltip>
    </ActionsBarStyles>
  );
};

const PaymentCard = ({ payment }) => {
  const theme = useTheme();
  const { isMobile } = useIsMobile();
  const { Icon, text: methodText } = formatMethod(payment);
  const amounts = getAmounts(payment);

  return (
    <Card variant="outlined" sx={{ width: "100%"}}>
      <CardHeader
        title={
          <TitleRow gap={isMobile ? "2rem" : "3.5rem"}>
            <div>{formatCurrency(amounts.amount)}</div>
            <Status>
              <MultiStatusChip py=".2rem" fs="1.2rem" value={getPaymentStatus(amounts)} values={statusValues} />
            </Status>
          </TitleRow>
        }
        subheader={`Payment #${payment.payment_num} · ${formatGenericDate(payment.date)}`}
        action={<ActionsBar payment={payment} />}
        sx={{ p: CARD_SPACING, pb: 0 }}
        slotProps={{
          title: { sx: { fontSize: isMobile ? "1.4rem" : "1.6rem" } },
          subheader: { sx: { fontSize: isMobile ? "1.2rem" : "1.2rem" } },
        }}
      />
      <CardContent
        sx={{
          p: CARD_SPACING,
          '&:last-child': { pb: CARD_SPACING },
          display: 'flex',
          flexDirection: 'column',
          gap: CARD_SPACING,
        }}
      >
        <Allocations allocations={payment.applied_payments} notes={payment.notes} />
        <BasicFlex justify="flex-start" gap={isMobile ? "2rem" : "5rem"} align="flex-start">
          <BasicFlex dir="column" width="auto" gap=".2rem">
            <LabelUpper $size="1.2rem">Method</LabelUpper>
            <MethodValue>
              <Icon fontSize="small" />
              {methodText}
            </MethodValue>
          </BasicFlex>
          <BasicFlex dir="column" width="auto" gap=".2rem">
            <LabelUpper $size="1.2rem">Applied</LabelUpper>
            <div>{formatCurrency(amounts.applied)}</div>
          </BasicFlex>
          <BasicFlex dir="column" width="auto" gap=".2rem">
            <LabelUpper $size="1.2rem">Available</LabelUpper>
            <div style={amounts.available > 0 ? { color: theme.palette.warning.main } : undefined}>
              {formatCurrency(amounts.available)}
            </div>
          </BasicFlex>
        </BasicFlex>
      </CardContent>
    </Card>
  );
};

const Payments = () => {
  const { data } = usePageData() || {};

  const { data: payments, pagination, setPagination } = useSimpleTable({ url: `/payments/customer/${data.customer_id}` });

  return (
    <Grid container spacing={3}>
      <Grid size={{ xs: 12, md: 8 }}>
        <Card elevation={0} sx={{backgroundColor: "transparent"}}>
          <CardHeader title="Payments" sx={{ p: 1 }} />

          <CardContent sx={{ p: 1 }}>
            <PaymentList dir="column" gap="1.5rem">
              {payments?.rows?.map((payment) => (
                <PaymentCard key={payment.payment_id} payment={payment} />
              ))}
            </PaymentList>
            <SimplePagination
              rowsPerPage={pagination?.rowsPerPage}
              currentPage={pagination?.page}
              setPagination={setPagination}
              resultCount={payments?.count}
              results={payments?.rows}
            />
          </CardContent>
        </Card>
      </Grid>
    </Grid>
  );
};

export default Payments;